import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DIGIT_HEIGHT = 36;

// Currency symbol mapping
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  AUD: "A$",
  GBP: "£",
  CAD: "C$",
};

const NumberColumn = ({ digit, index }: { digit: number; index: number }) => {
  // Force animation to always start from 9 (bottom of the list)
  // This ensures a visible animation regardless of the final digit
  const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div
      className="tw-relative tw-overflow-hidden"
      style={{ height: `${DIGIT_HEIGHT}px`, width: `${DIGIT_HEIGHT * 0.5}px` }}
    >
      <motion.div
        initial={{ y: -9 * DIGIT_HEIGHT }} // Always start from 9
        animate={{ y: -digit * DIGIT_HEIGHT }}
        transition={{
          type: "tween",
          duration: 2,
          ease: [0.16, 0.9, 0.3, 1],
          delay: index * 0.3,
        }}
        className="tw-absolute tw-left-0 tw-right-0"
      >
        {numbers.map((num) => (
          <div
            key={num}
            className="tw-flex tw-items-center tw-justify-center tw-text-3xl tw-font-bold"
            style={{ height: `${DIGIT_HEIGHT}px` }}
          >
            {num}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

interface AnimatedPriceProps {
  isLoading: boolean;
  price: number | null;
  currencyIso?: string;
}

export default function AnimatedPrice({
  isLoading,
  price,
  currencyIso = "USD",
}: AnimatedPriceProps) {
  const [displayValue, setDisplayValue] = useState("000.00");
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [showLoading, setShowLoading] = useState(isLoading);

  const currencySymbol = CURRENCY_SYMBOLS[currencyIso] || "$";

  // Handle loading/loaded state transitions carefully
  useEffect(() => {
    if (isLoading !== showLoading) {
      if (isLoading) {
        // Transitioning to loading state - show immediately
        setShowLoading(true);
      } else {
        // Transitioning from loading to price - add exit delay
        setIsExiting(true);
        setTimeout(() => {
          setShowLoading(false);
          setIsExiting(false);
          setShouldAnimate(true); // Trigger animation when showing price
        }, 500); // Match this with the exit animation duration
      }
    }

    // Rest of the existing useEffect logic
    if (isLoading) {
      const interval = setInterval(() => {
        const randomValue = (Math.random() * 200 + 50).toFixed(2);
        setDisplayValue(randomValue);
      }, 200);
      return () => clearInterval(interval);
    } else if (price !== null) {
      if (price === 0) {
        setDisplayValue("0");
      } else {
        // Check if the number is a whole number (no decimal part)
        const isWholeNumber = price % 1 === 0;
        if (isWholeNumber) {
          setDisplayValue(price.toString());
        } else {
          // For numbers with decimal parts, round to 2 decimal place if needed
          setDisplayValue(price.toFixed(2).replace(/\.0$/, ""));
        }
      }
    }
  }, [isLoading, price, showLoading]);

  // Reset animation flag after delay
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);

  // Handle digits display differently for zero price
  const isZeroPrice = !isLoading && price === 0;
  const digits = isZeroPrice ? ["0"] : displayValue.split("");

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
      <div
        className="tw-relative tw-flex tw-items-center tw-justify-center"
        style={{ height: `${DIGIT_HEIGHT}px` }}
      >
        <AnimatePresence>
          {showLoading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? -10 : 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="tw-flex tw-items-center tw-absolute"
            >
              {/* Loading animation content - same as before */}
              <div className="tw-flex tw-space-x-2 tw-items-center">
                <span className="tw-text-3xl tw-font-bold tw-text-gray-400">
                  {currencySymbol}
                </span>
                <div className="tw-flex tw-space-x-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="tw-w-4 tw-bg-gray-200 tw-rounded"
                      style={{ height: `${DIGIT_HEIGHT}px` }}
                      animate={{
                        height: [
                          `${DIGIT_HEIGHT * 0.4}px`,
                          `${DIGIT_HEIGHT}px`,
                          `${DIGIT_HEIGHT * 0.4}px`,
                        ],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Number.POSITIVE_INFINITY,
                        delay: i * 0.3,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {!showLoading && (
            <motion.div
              key={`price-${shouldAnimate ? "animate" : "static"}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="tw-flex tw-items-center tw-absolute"
            >
              <span className="tw-text-3xl tw-font-bold tw-text-gray-600 tw-mr-1">
                {currencySymbol}
              </span>
              <div className="tw-flex tw-text-gray-600">
                {digits.map((digit, index) => (
                  <React.Fragment key={index}>
                    {digit === "." ? (
                      <div className="tw-text-3xl tw-font-bold">.</div>
                    ) : (
                      <NumberColumn
                        digit={Number.parseInt(digit)}
                        index={index}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
